import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Eye, Download, Search, Filter, Calendar, User } from 'lucide-react';
import './AdminCVManagement.scss';

interface CVRecord {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  user_email: string;
  user_type: string;
  created_at: string;
  updated_at: string;
  work_experience: any[];
  education: any[];
  language_skills: any[];
  computer_skills: any;
  job_preferences: any;
}

const AdminCVManagement: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [cvs, setCvs] = useState<CVRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCV, setSelectedCV] = useState<CVRecord | null>(null);
  const [showCVModal, setShowCVModal] = useState(false);

  const fetchAllCVs = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setCvs(result.data || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch CVs:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'admin') {
      fetchAllCVs();
    }
  }, [isAuthenticated, user]);

  // Redirect if not authenticated or not an admin
  if (!isAuthenticated || user?.user_type !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const handleViewCV = (cv: CVRecord) => {
    setSelectedCV(cv);
    setShowCVModal(true);
  };

  const handleCloseModal = () => {
    setShowCVModal(false);
    setSelectedCV(null);
  };

  const filteredCVs = cvs.filter(cv => 
    cv.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cv.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cv.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cv.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const getExperienceYears = (workExperience: any[]) => {
    if (!workExperience || workExperience.length === 0) return 'Nenurodyta';
    return `${workExperience.length} darbo vietos`;
  };

  const getEducationLevel = (education: any[]) => {
    if (!education || education.length === 0) return 'Nenurodyta';
    const latest = education[0];
    return latest?.educationLevel || latest?.degree || 'Nenurodyta';
  };

  if (loading) {
    return (
      <div className="admin-cv-management">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Kraunami CV duomenys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-cv-management">
      <div className="page-header">
        <h1>CV valdymas</h1>
        <p>Peržiūrėkite ir valdykite visų darbuotojų CV</p>
      </div>

      <div className="cv-management-content">
        {/* Search and Filter Bar */}
        <div className="search-filter-bar">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Ieškoti pagal vardą, pavardę ar el. paštą..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button className="filter-btn">
              <Filter size={16} />
              Filtrai
            </button>
          </div>
        </div>

        {/* CV Statistics */}
        <div className="cv-stats">
          <div className="stat-card">
            <div className="stat-icon">📄</div>
            <div className="stat-info">
              <h3>{cvs.length}</h3>
              <p>Iš viso CV</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🆕</div>
            <div className="stat-info">
              <h3>{cvs.filter(cv => new Date(cv.updated_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}</h3>
              <p>Atnaujinti per savaitę</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <h3>{cvs.filter(cv => cv.work_experience && cv.work_experience.length > 0).length}</h3>
              <p>Su darbo patirtimi</p>
            </div>
          </div>
        </div>

        {/* CV List */}
        <div className="cv-list">
          <div className="cv-list-header">
            <h2>Darbuotojų CV sąrašas ({filteredCVs.length})</h2>
          </div>
          
          {filteredCVs.length === 0 ? (
            <div className="empty-state">
              <p>CV nerasta pagal paieškos kriterijus</p>
            </div>
          ) : (
            <div className="cv-table">
              <div className="table-header">
                <div className="col-user">Darbuotojas</div>
                <div className="col-contact">Kontaktai</div>
                <div className="col-experience">Patirtis</div>
                <div className="col-education">Išsilavinimas</div>
                <div className="col-updated">Atnaujinta</div>
                <div className="col-actions">Veiksmai</div>
              </div>
              
              {filteredCVs.map((cv) => (
                <div key={cv.id} className="table-row">
                  <div className="col-user">
                    <div className="user-info">
                      <div className="user-avatar">
                        <User size={20} />
                      </div>
                      <div className="user-details">
                        <h4>{cv.first_name} {cv.last_name}</h4>
                        <span className="user-email">{cv.user_email}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-contact">
                    <div className="contact-info">
                      <div>{cv.email}</div>
                      <div>{cv.phone}</div>
                      <div className="location">{cv.address}</div>
                    </div>
                  </div>
                  
                  <div className="col-experience">
                    {getExperienceYears(cv.work_experience)}
                  </div>
                  
                  <div className="col-education">
                    {getEducationLevel(cv.education)}
                  </div>
                  
                  <div className="col-updated">
                    <div className="date-info">
                      <Calendar size={14} />
                      {formatDate(cv.updated_at)}
                    </div>
                  </div>
                  
                  <div className="col-actions">
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handleViewCV(cv)}
                    >
                      <Eye size={16} />
                      Peržiūrėti
                    </button>
                    <button className="action-btn download-btn">
                      <Download size={16} />
                      Atsisiųsti
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CV Detail Modal */}
      {showCVModal && selectedCV && (
        <div className="cv-modal-overlay" onClick={handleCloseModal}>
          <div className="cv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cv-modal-header">
              <h2>{selectedCV.first_name} {selectedCV.last_name} - CV</h2>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="cv-modal-content">
              {/* Personal Information */}
              <div className="cv-section">
                <h3>Asmeninė informacija</h3>
                <div className="info-grid">
                  <div><strong>Vardas:</strong> {selectedCV.first_name}</div>
                  <div><strong>Pavardė:</strong> {selectedCV.last_name}</div>
                  <div><strong>El. paštas:</strong> {selectedCV.email}</div>
                  <div><strong>Telefonas:</strong> {selectedCV.phone}</div>
                  <div><strong>Adresas:</strong> {selectedCV.address}</div>
                </div>
              </div>

              {/* Work Experience */}
              {selectedCV.work_experience && selectedCV.work_experience.length > 0 && (
                <div className="cv-section">
                  <h3>Darbo patirtis</h3>
                  {selectedCV.work_experience.map((exp: any, index: number) => (
                    <div key={index} className="experience-item">
                      <div className="exp-header">
                        <h4>{exp.position} - {exp.companyName}</h4>
                        <span className="exp-period">{exp.period}</span>
                      </div>
                      {exp.workNature && <p>{exp.workNature}</p>}
                      {exp.achievements && <p><strong>Pasiekimai:</strong> {exp.achievements}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Education */}
              {selectedCV.education && selectedCV.education.length > 0 && (
                <div className="cv-section">
                  <h3>Išsilavinimas</h3>
                  {selectedCV.education.map((edu: any, index: number) => (
                    <div key={index} className="education-item">
                      <div className="edu-header">
                        <h4>{edu.institutionName}</h4>
                        <span className="edu-period">{edu.studyPeriod}</span>
                      </div>
                      <p>{edu.educationLevel} - {edu.fieldOfStudy}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Language Skills */}
              {selectedCV.language_skills && selectedCV.language_skills.length > 0 && (
                <div className="cv-section">
                  <h3>Kalbų įgūdžiai</h3>
                  <div className="language-skills">
                    {selectedCV.language_skills.map((lang: any, index: number) => (
                      <div key={index} className="language-item">
                        <strong>{lang.language}:</strong>
                        <span> Supratimas: {lang.understanding}/5, </span>
                        <span> Kalbėjimas: {lang.speaking}/5, </span>
                        <span> Rašymas: {lang.writing}/5</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Job Preferences */}
              {selectedCV.job_preferences && (
                <div className="cv-section">
                  <h3>Pageidaujamas darbas</h3>
                  <div className="info-grid">
                    {selectedCV.job_preferences.workLocation && (
                      <div><strong>Darbo vieta:</strong> {selectedCV.job_preferences.workLocation}</div>
                    )}
                    {selectedCV.job_preferences.workField && (
                      <div><strong>Darbo sritis:</strong> {selectedCV.job_preferences.workField}</div>
                    )}
                    {selectedCV.job_preferences.position && (
                      <div><strong>Pareigos:</strong> {selectedCV.job_preferences.position}</div>
                    )}
                    {selectedCV.job_preferences.salary && (
                      <div><strong>Atlyginimas:</strong> {selectedCV.job_preferences.salary}</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="cv-modal-footer">
              <button className="modal-btn secondary" onClick={handleCloseModal}>
                Uždaryti
              </button>
              <button className="modal-btn primary">
                <Download size={16} />
                Atsisiųsti PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCVManagement;
